import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './AuthContext';
import Login from './Login';
import UsersAccess from './components/UsersAccess';
import { fetchWithAuth } from './services/api';
import BasicPropertyInfo from './components/BasicPropertyInfo';
import ViolationsTable from './components/ViolationsTable';
import ComparableProperties from './components/ComparableProperties';
import PriceAnalysis from './components/PriceAnalysis';
import PriceTrendChart from './components/PriceTrendChart';
import AssetModel from './components/AssetModel';
import DelinquencyTable from './components/DelinquencyTable';
import PortfolioTable from './components/PortfolioTable';
import Maintenance from './components/Maintenance';
import Performance from './components/Performance';
import Vacancy from './components/Vacancy';
import VacancySpend from './components/VacancySpend';
import Analytics from './components/Analytics';
import TenantRentCollection from './components/TenantRentCollection';
import LeaseAnalysis from './components/LeaseAnalysis';
import ExpensesPage from './components/ExpensesPage';
import WorkyardPage from './components/WorkyardPage';
import LcaExpensesPage from './components/LcaExpensesPage';
import LowesPage from './components/LowesPage';
import MortgageExpensesPage from './components/MortgageExpensesPage';


function PropertyWorkspace({ tab, initialAddress }) {
  const [searchValue, setSearchValue] = useState(initialAddress || '');
  const [loading, setLoading] = useState({ basic: false, comps: false, historical: false });
  const [error, setError] = useState(null);
  const [data, setData] = useState({});
  const fetchBasicInfo = async (searchValue) => {
    setLoading(prev => ({ ...prev, basic: true }));
    try {
      const response = await fetchWithAuth(`/api/property-basic?search=${encodeURIComponent(searchValue)}`);
      const result = await response.json();
      if (!response.ok) throw new Error(result.error);
      
      setData(prev => ({
        ...prev,
        reference_property: result.reference_property,
        payment_standards: result.payment_standards,
        violations: result.violations
      }));
  
      // Trigger loading of additional data
      fetchComps(result.opa_number, result.reference_property);
      fetchHistorical(result.opa_number, result.reference_property);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(prev => ({ ...prev, basic: false }));
    }
  };

  const fetchComps = async (opaNumber, referenceProperty) => {
    setLoading(prev => ({ ...prev, comps: true }));
    try {
      const response = await fetchWithAuth('/api/property-comps', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          opa_number: opaNumber,
          reference: referenceProperty
        })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error);
      
      setData(prev => ({
        ...prev,
        comparable_properties: result.comparable_properties,
        statistics: result.statistics
      }));
    } catch (err) {
      console.error('Error fetching comps:', err);
    } finally {
      setLoading(prev => ({ ...prev, comps: false }));
    }
  };

  const fetchHistorical = async (opaNumber, referenceProperty) => {
    setLoading(prev => ({ ...prev, historical: true }));
    try {
      const response = await fetchWithAuth('/api/property-historical', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          opa_number: opaNumber,
          reference: referenceProperty
        })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error);
      
      setData(prev => ({
        ...prev,
        historical_trend: result.historical_trend
      }));
    } catch (err) {
      console.error('Error fetching historical data:', err);
    } finally {
      setLoading(prev => ({ ...prev, historical: false }));
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchValue) return;
    
    setError(null);
    setData({
      reference_property: null,
      violations: null,
      comparable_properties: null,
      statistics: null,
      historical_trend: null
    });
    
    await fetchBasicInfo(searchValue);
  };

  // Preserve the Portfolio address shortcut without refetching on subtab changes.
  const initialSearch = useRef();
  initialSearch.current = fetchBasicInfo;
  useEffect(() => {
    if (initialAddress) initialSearch.current(initialAddress);
  }, [initialAddress]);

  const formatCurrency = (value) => {
    if (!value) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatNumber = (value) => {
    if (!value) return 'N/A';
    return new Intl.NumberFormat('en-US').format(value);
  };


  return <section>
    <form onSubmit={handleSearch} className="flex gap-3 max-w-2xl mx-auto my-8">
      <input value={searchValue} onChange={e => setSearchValue(e.target.value)}
        aria-label="Property address or OPA number" placeholder="Enter address or OPA number"
        className="flex-1 border rounded px-4 py-2" />
      <button disabled={loading.basic} className="bg-blue-600 text-white rounded px-5 py-2">
        {loading.basic ? 'Searching…' : 'Search'}
      </button>
    </form>
    {error && <p role="alert" className="text-red-700">{error}</p>}
    {data.reference_property && <>
      {tab === 'property.information' ? <>
        <BasicPropertyInfo property={data.reference_property} formatNumber={formatNumber} formatCurrency={formatCurrency} />
        {data.violations?.length > 0 && <ViolationsTable violations={data.violations} />}
      </> : <AssetModel property={data.reference_property} paymentStandards={data.payment_standards} />}
      <div className="mt-8">
        {loading.comps ? <p>Loading comparable properties…</p> : data.comparable_properties && <>
          <PriceAnalysis stats={data.statistics} />
          {loading.historical ? <p>Loading historical trends…</p> : data.historical_trend && <PriceTrendChart historicalTrend={data.historical_trend} />}
          <ComparableProperties comps={data.comparable_properties} stats={data.statistics}
            formatNumber={formatNumber} formatCurrency={formatCurrency} />
        </>}
      </div>
    </>}
  </section>;
}

const PAGES = {
  'portfolio.overview': PortfolioTable,
  'portfolio.performance': Performance,
  'portfolio.analytics': Analytics,
  'portfolio.rent_collection': TenantRentCollection,
  'portfolio.vacancy_spend': VacancySpend,
  'tenants.delinquency': DelinquencyTable,
  'tenants.vacancy': Vacancy,
  'tenants.lease_analysis': LeaseAnalysis,
  'expenses.amex': ExpensesPage,
  'expenses.workyard': WorkyardPage,
  'expenses.lca': LcaExpensesPage,
  'expenses.lowes': LowesPage,
  'expenses.mortgages': MortgageExpensesPage,
};
export function Workspace() {
  const { user, catalog, can, logout } = useAuth();
  const [selected, setSelected] = useState(null);
  const [propertyAddress, setPropertyAddress] = useState('');
  const [message, setMessage] = useState('');
  const available = catalog.map(tab => ({ ...tab, children: tab.children.filter(child => can(child.id)) }))
    .filter(tab => tab.children.length);
  const adminSelected = selected === 'admin' && user.manage_users;
  const current = available.flatMap(tab => tab.children).find(child => child.id === selected)?.id
    || available[0]?.children[0]?.id;
  const parent = available.find(tab => tab.children.some(child => child.id === current));
  const Page = PAGES[current];
  return <div className="container mx-auto px-4 py-8">
    <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
      <nav aria-label="Main tabs" className="flex flex-wrap border rounded-lg overflow-hidden">
        {available.map(tab => <button key={tab.id} onClick={() => setSelected(tab.children[0].id)}
          className={'px-6 py-2 ' + (!adminSelected && parent?.id === tab.id ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50')}>
          {tab.label}
        </button>)}
        {user.manage_users && <button onClick={() => setSelected('admin')}
          className={'px-6 py-2 ' + (adminSelected ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50')}>Admin</button>}
      </nav>
      <div className="flex items-center gap-3"><span className="text-sm text-gray-500">{user.name}</span>
        <button className="border rounded px-3 py-2" onClick={() => logout().catch(e => setMessage(e.message))}>Sign out</button>
      </div>
    </div>
    {message && <p role="alert">{message}</p>}
    {adminSelected ? <UsersAccess /> : current ? <>
      <nav aria-label="Subtabs" className="flex flex-wrap border-b mb-6">
        {parent.children.map(child => <button key={child.id} onClick={() => setSelected(child.id)}
          className={'px-4 py-3 ' + (current === child.id ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600')}>
          {child.label}
        </button>)}
      </nav>
      {parent.id === 'maintenance' ? <Maintenance view={current.split('.')[1]} /> : parent.id === 'property' ? <PropertyWorkspace tab={current} initialAddress={propertyAddress} /> : Page ? <Page
        onPropertySelect={available.some(t => t.id === 'property') ? address => {
          setPropertyAddress(address);
          setSelected(available.find(t => t.id === 'property').children[0].id);
        } : undefined} /> : <p>This page is not yet available.</p>}
    </> : <p className="p-8 bg-gray-50 rounded">Your account is approved, but no website tabs are enabled. Contact Artem or Nathan.</p>}
  </div>;
}
function ProtectedWorkspace() {
  const { user, loading, error, refresh } = useAuth();
  if (loading) return <p className="p-8">Checking access…</p>;
  if (error) return <div className="p-8" role="alert">{error} <button onClick={refresh} className="underline">Retry</button></div>;
  if (!user) return <Navigate to="/login" replace />;
  // Clear previous users' or revoked tabs' component data on access changes.
  return <Workspace key={user.id + ':' + user.version} />;
}
export default function App() {
  return <Router><AuthProvider><Routes>
    <Route path="/login" element={<Login />} />
    <Route path="/*" element={<ProtectedWorkspace />} />
  </Routes></AuthProvider></Router>;
}
